/* ============================================================
   CHICKS — puerta de edad 18+
   Custom element al estilo de Dawn: `class X extends HTMLElement`
   más `customElements.define`.

   El marcado completo llega desde Liquid. Este archivo no pinta
   nada: decide si la puerta se enciende, atrapa el foco mientras
   está abierta y guarda la respuesta.

   EL ESTADO DE BLOQUEO LO PONE ESTE SCRIPT
   `data-ch-puerta` en el <html>, igual que chicks-motion.js con
   `data-ch-motion`. El CSS no esconde nada por su cuenta. Si este
   archivo no carga, falla al descargarse o revienta a la mitad, la
   puerta no aparece y la tienda queda usable y comprable. Ese es el
   trato: preferimos una puerta que no se ve a una tienda que no se
   puede usar.

   ESCAPE NO CIERRA
   Es una puerta, no un modal opcional. La tecla se traga a propósito.

   Va envuelto en una función y con guardia de `customElements.get`
   porque el editor de temas puede reinyectar el layout.
   ============================================================ */

(function () {
  'use strict';

  if (customElements.get('chicks-puerta-edad')) return;

  /* La clave lleva versión: si algún día cambia lo que se pregunta,
     se sube a `-v2` y todo el mundo la vuelve a contestar. */
  var CLAVE = 'chicks:puerta-edad:v1';
  var VALOR = 'mayor';

  /* Elementos que sí pueden recibir foco dentro del diálogo. */
  var ENFOCABLES = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /* Sólo una puerta por página, aunque el snippet se renderice dos
     veces por descuido. */
  var yaAbierta = false;

  /* --- memoria ---------------------------------------------------
     `localStorage` truena, no devuelve null, cuando el navegador
     tiene el almacenamiento bloqueado (Safari en privado, cookies de
     terceros apagadas dentro del editor de temas). Un throw aquí
     dejaría la puerta abierta para siempre, así que todo va en
     try/catch y con `sessionStorage` de respaldo: en el peor caso se
     recuerda mientras dure la pestaña, no se rompe nada. */

  function yaContesto() {
    try {
      if (window.localStorage && localStorage.getItem(CLAVE) === VALOR) return true;
    } catch (e) {
      /* almacenamiento bloqueado: se sigue de largo */
    }
    try {
      if (window.sessionStorage && sessionStorage.getItem(CLAVE) === VALOR) return true;
    } catch (e) {
      /* igual */
    }
    return false;
  }

  function recordar() {
    try {
      localStorage.setItem(CLAVE, VALOR);
      return;
    } catch (e) {
      /* se intenta el respaldo */
    }
    try {
      sessionStorage.setItem(CLAVE, VALOR);
    } catch (e) {
      /* nada que hacer: la puerta igual se abre en esta visita */
    }
  }

  /* Para revisar la puerta después de haberla aceptado: ?ch-puerta=abrir */
  function forzada() {
    return /[?&]ch-puerta=abrir(?:&|$)/.test(window.location.search);
  }

  class ChicksPuertaEdad extends HTMLElement {
    constructor() {
      super();
      this.alTeclear = this.alTeclear.bind(this);
      this.alEnfocar = this.alEnfocar.bind(this);
    }

    connectedCallback() {
      if (this.montada) return;
      this.montada = true;

      this.dialogo = this.querySelector('[data-ch-puerta-dialogo]');
      this.vistaPregunta = this.querySelector('[data-ch-puerta-vista="pregunta"]');
      this.vistaAdios = this.querySelector('[data-ch-puerta-vista="adios"]');
      this.botonSi = this.querySelector('[data-ch-puerta-si]');
      this.botonNo = this.querySelector('[data-ch-puerta-no]');
      this.botonVolver = this.querySelector('[data-ch-puerta-volver]');
      this.tituloAdios = this.querySelector('[data-ch-puerta-adios-foco]');

      // Si falta una pieza del marcado, no se bloquea nada. Media
      // puerta es peor que ninguna.
      if (!this.dialogo || !this.botonSi) return;

      if (yaAbierta) return;
      if (!forzada() && yaContesto()) return;

      this.botonSi.addEventListener('click', () => this.dejarPasar());
      if (this.botonNo) this.botonNo.addEventListener('click', () => this.despedir());
      if (this.botonVolver) this.botonVolver.addEventListener('click', () => this.preguntarDeNuevo());

      this.abrir();
    }

    /* --- abrir / cerrar ------------------------------------------ */

    abrir() {
      yaAbierta = true;
      this.enfocadoAntes = document.activeElement;

      var raiz = document.documentElement;

      // Medir ANTES de bloquear: después ya no hay barra que medir.
      // Sin esta compensación, quitar el scroll corre el contenido el
      // ancho de la barra y eso cuenta como salto de layout.
      var barra = window.innerWidth - raiz.clientWidth;
      if (barra > 0) raiz.style.setProperty('--ch-puerta-barra', barra + 'px');

      raiz.setAttribute('data-ch-puerta', '');

      // En captura: así llegan antes que los manejadores globales de
      // Dawn, que cierran cosas con Escape.
      document.addEventListener('keydown', this.alTeclear, true);
      document.addEventListener('focusin', this.alEnfocar, true);

      this.botonSi.focus();
    }

    cerrar() {
      var raiz = document.documentElement;
      raiz.removeAttribute('data-ch-puerta');
      raiz.style.removeProperty('--ch-puerta-barra');

      document.removeEventListener('keydown', this.alTeclear, true);
      document.removeEventListener('focusin', this.alEnfocar, true);

      yaAbierta = false;
      this.devolverFoco();
    }

    dejarPasar() {
      recordar();
      this.cerrar();
    }

    /* «Todavía no» NO se guarda: nadie queda bloqueado para siempre
       por un dedazo, y una respuesta negativa guardada tampoco prueba
       nada. Se cambia de vista y el foco se va al encabezado nuevo
       para que el lector de pantalla lo anuncie. */
    despedir() {
      if (!this.vistaAdios || !this.vistaPregunta) return;

      this.vistaPregunta.hidden = true;
      this.vistaAdios.hidden = false;

      if (this.tituloAdios) {
        this.dialogo.setAttribute('aria-labelledby', this.tituloAdios.id);
        this.dialogo.removeAttribute('aria-describedby');
        this.tituloAdios.focus();
      }
    }

    preguntarDeNuevo() {
      if (!this.vistaAdios || !this.vistaPregunta) return;

      this.vistaAdios.hidden = true;
      this.vistaPregunta.hidden = false;

      this.dialogo.setAttribute('aria-labelledby', 'ChicksPuertaTitulo');
      this.dialogo.setAttribute('aria-describedby', 'ChicksPuertaTexto');
      this.botonSi.focus();
    }

    /* --- foco ---------------------------------------------------- */

    enfocables() {
      var todos = this.querySelectorAll(ENFOCABLES);
      var visibles = [];
      for (var i = 0; i < todos.length; i++) {
        // `getClientRects` en vez de `offsetParent`: dentro de un
        // ancestro `position: fixed`, offsetParent miente.
        if (todos[i].getClientRects().length > 0) visibles.push(todos[i]);
      }
      return visibles;
    }

    alTeclear(evento) {
      if (evento.key === 'Escape' || evento.key === 'Esc') {
        // Es una puerta, no un modal opcional. Se traga la tecla aquí
        // para que nadie más la escuche.
        evento.preventDefault();
        evento.stopPropagation();
        return;
      }

      if (evento.key !== 'Tab') return;

      var focos = this.enfocables();
      if (focos.length === 0) {
        evento.preventDefault();
        this.dialogo.focus();
        return;
      }

      var primero = focos[0];
      var ultimo = focos[focos.length - 1];
      var activo = document.activeElement;
      var adentro = this.contains(activo);

      if (evento.shiftKey && (activo === primero || !adentro)) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && (activo === ultimo || !adentro)) {
        evento.preventDefault();
        primero.focus();
      }
    }

    /* Red de seguridad del atrapado: si el foco se escapa por donde
       sea —la barra del navegador, un iframe, un script ajeno— se
       regresa al diálogo. */
    alEnfocar(evento) {
      if (this.contains(evento.target)) return;

      var focos = this.enfocables();
      if (focos.length > 0) {
        focos[0].focus();
      } else {
        this.dialogo.focus();
      }
    }

    devolverFoco() {
      var previo = this.enfocadoAntes;
      if (previo && document.contains(previo) && typeof previo.focus === 'function') {
        previo.focus();
        return;
      }

      // La puerta abrió al cargar, así que no había foco previo real:
      // se manda al principio del documento para que quien usa lector
      // de pantalla empiece por el encabezado y no a media página.
      var cuerpo = document.body;
      cuerpo.setAttribute('tabindex', '-1');
      cuerpo.focus();
      cuerpo.removeAttribute('tabindex');
    }
  }

  customElements.define('chicks-puerta-edad', ChicksPuertaEdad);
})();
